import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  Copy, 
  ExternalLink, 
  Download,
  Filter,
  RefreshCw,
  CreditCard,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  ChevronDown,
  ChevronUp,
  PieChart as PieChartIcon,
  BarChart3,
  Tag
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { getAffiliateDashboardSEO, validateSEOData, generateKeywords } from '@/services/seoService';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/lib/AuthProvider';
import { useUserProfile } from '@/context/UserProfileContext';
import { useAffiliate } from '@/context/AffiliateContext';
import AuthModalWrapper from '@/components/AuthModalWrapper';
import { formatCurrency } from '@/lib/utils';
import { TablePagination } from '@/components/TablePagination';
import LoadingSpinner from '@/components/LoadingSpinner';

const AffiliateDashboard = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [filteredStats, setFilteredStats] = useState({
    clicks: 0,
    conversions: 0,
    totalEarnings: 0
  });
  const [filteredCommissions, setFilteredCommissions] = useState<any[]>([]);
  const [filteredPayouts, setFilteredPayouts] = useState<any[]>([]);
  const [isLoadingFiltered, setIsLoadingFiltered] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true); // Collapsed by default
  
  // Pagination state for commissions
  const [commissionsCurrentPage, setCommissionsCurrentPage] = useState(1);
  const [commissionsPageSize] = useState(16);
  
  // Pagination state for payouts
  const [payoutsCurrentPage, setPayoutsCurrentPage] = useState(1);
  const [payoutsPageSize] = useState(16);
  
  // Commission analytics state
  const [sourceAnalytics, setSourceAnalytics] = useState<any>(null);
  const [discountCodes, setDiscountCodes] = useState<any[]>([]);
  const [discountPerformance, setDiscountPerformance] = useState<any[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  const { user } = useAuthContext();
  const { userProfile } = useUserProfile();
  const { 
    application, 
    dashboardData, 
    loading, 
    error,
    refreshDashboard,
    getCommissionsInDateRange,
    getStatsInDateRange,
    getCommissionSourceAnalytics,
    getAffiliateDiscountCodes,
    getDiscountCodePerformance
  } = useAffiliate();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!user) {
      setAuthModalOpen(true);
    }
  }, [user]);

  // Redirect if not an affiliate or application not approved
  useEffect(() => {
    if (user && userProfile && (!userProfile.isAffiliate || (application && application.status !== 'approved'))) {
      navigate('/affiliate/signup');
    }
  }, [user, userProfile, application, navigate]);

  // Initialize with empty date filter to show all-time data by default
  useEffect(() => {
    setDateFilter({
      startDate: '',
      endDate: ''
    });
  }, []);

  // Auto-scroll to top when loading completes
  useEffect(() => {
    if (!loading && dashboardData) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [loading, dashboardData]);

  // Load analytics data when dashboard loads
  useEffect(() => {
    if (dashboardData && application) {
      loadAnalyticsData();
    }
  }, [dashboardData, application]);

  // Load filtered data when date filter changes (only if both dates are provided)
  useEffect(() => {
    if (dateFilter.startDate && dateFilter.endDate && application) {
      loadFilteredData();
    } else if (!dateFilter.startDate && !dateFilter.endDate) {
      // Clear filtered data when no date filter is set
      setFilteredStats({ clicks: 0, conversions: 0, totalEarnings: 0 });
      setFilteredCommissions([]);
      setFilteredPayouts([]);
      setActiveFilter(null);
    }
    // Reset pagination when filter changes
    setCommissionsCurrentPage(1);
    setPayoutsCurrentPage(1);
  }, [dateFilter, application]);

  const loadFilteredData = async () => {
    if (!dateFilter.startDate || !dateFilter.endDate || !application) return;

    try {
      setIsLoadingFiltered(true);
      
      // Create dates in local timezone to avoid timezone issues
      const startDate = new Date(dateFilter.startDate + 'T00:00:00');
      const endDate = new Date(dateFilter.endDate + 'T23:59:59');

      const [stats, commissions] = await Promise.all([
        getStatsInDateRange(startDate, endDate),
        getCommissionsInDateRange(startDate, endDate)
      ]);

      // Filter payouts based on date range
      const filteredPayoutRecords = dashboardData?.payoutRecords?.filter(payout => {
        const payoutDate = new Date(payout.paidAt);
        return payoutDate >= startDate && payoutDate <= endDate;
      }) || [];

      setFilteredStats(stats);
      setFilteredCommissions(commissions);
      setFilteredPayouts(filteredPayoutRecords);
      
      // Load analytics for filtered period
      loadAnalyticsData(startDate, endDate);
    } catch (error) {
      console.error('Error loading filtered data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load filtered data',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingFiltered(false);
    }
  };

  const loadAnalyticsData = async (startDate?: Date, endDate?: Date) => {
    if (!application) return;

    try {
      setAnalyticsLoading(true);
      
      const [analytics, codes, performance] = await Promise.all([
        getCommissionSourceAnalytics(startDate, endDate),
        getAffiliateDiscountCodes(),
        getDiscountCodePerformance(startDate, endDate)
      ]);

      setSourceAnalytics(analytics);
      setDiscountCodes(codes);
      setDiscountPerformance(performance);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (!application?.referralCode) return;
    
    const referralLink = `https://jampackd.store?ref=${application.referralCode}`;
    navigator.clipboard.writeText(referralLink);
    
    toast({
      title: 'Copied!',
      description: 'Referral link copied to clipboard',
    });
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Pagination utility functions
  const getPaginatedCommissions = () => {
    const allCommissions = dateFilter.startDate && dateFilter.endDate 
      ? filteredCommissions 
      : dashboardData?.commissionRecords || [];
    
    const startIndex = (commissionsCurrentPage - 1) * commissionsPageSize;
    const endIndex = startIndex + commissionsPageSize;
    return allCommissions.slice(startIndex, endIndex);
  };

  const getPaginatedPayouts = () => {
    const allPayouts = dateFilter.startDate && dateFilter.endDate 
      ? filteredPayouts 
      : dashboardData?.payoutRecords || [];
    
    const startIndex = (payoutsCurrentPage - 1) * payoutsPageSize;
    const endIndex = startIndex + payoutsPageSize;
    return allPayouts.slice(startIndex, endIndex);
  };

  const getCommissionsTotalPages = () => {
    const allCommissions = dateFilter.startDate && dateFilter.endDate 
      ? filteredCommissions 
      : dashboardData?.commissionRecords || [];
    return Math.ceil(allCommissions.length / commissionsPageSize);
  };

  const getPayoutsTotalPages = () => {
    const allPayouts = dateFilter.startDate && dateFilter.endDate 
      ? filteredPayouts 
      : dashboardData?.payoutRecords || [];
    return Math.ceil(allPayouts.length / payoutsPageSize);
  };

  // Page change handlers
  const handleCommissionsPageChange = (page: number) => {
    setCommissionsCurrentPage(page);
    // Scroll to commissions table
    const commissionsElement = document.getElementById('commissions-table');
    if (commissionsElement) {
      commissionsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePayoutsPageChange = (page: number) => {
    setPayoutsCurrentPage(page);
    // Scroll to payouts table
    const payoutsElement = document.getElementById('payouts-table');
    if (payoutsElement) {
      payoutsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-2xl transform rotate-45 animate-pulse" style={{ animationDelay: '300ms' }}></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/20 rounded-full animate-bounce" style={{ animationDelay: '500ms' }}></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/15 to-jamaica-green/15 rounded-3xl transform rotate-12 animate-ping" style={{ animationDelay: '700ms' }}></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/15 rounded-lg transform -rotate-12 animate-pulse" style={{ animationDelay: '900ms' }}></div>
          
          {/* Triangle Decorative Elements */}
          <div 
            className="absolute top-1/4 right-10 animate-pulse" 
            style={{ 
              animationDelay: '1000ms',
              width: 0,
              height: 0,
              borderLeft: '20px solid transparent',
              borderRight: '20px solid transparent',
              borderBottom: '35px solid rgba(0, 116, 47, 0.15)'
            }}
          ></div>
          <div 
            className="absolute bottom-1/3 left-16 animate-bounce" 
            style={{ 
              animationDelay: '1200ms',
              width: 0,
              height: 0,
              borderLeft: '15px solid transparent',
              borderRight: '15px solid transparent',
              borderBottom: '26px solid rgba(255, 201, 1, 0.2)'
            }}
          ></div>
          
          {/* Gradient Blobs */}
          <div className="absolute -top-20 -left-20 w-40 h-40 bg-jamaica-green/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-jamaica-yellow/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse" style={{ animationDelay: '1000ms' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-jamaica-green/5 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-ping" style={{ animationDelay: '500ms' }}></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(0, 116, 47, 0.08) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 116, 47, 0.08) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
        </div>

        {/* Main Loading Container */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="relative backdrop-blur-sm bg-white/80 rounded-3xl shadow-2xl border border-white/40 p-12 w-full max-w-md transform hover:scale-105 transition-all duration-500">
            {/* Decorative Elements */}
            <div className="absolute -top-6 -left-6 w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl shadow-xl transform rotate-12 animate-bounce"></div>
            <div className="absolute -top-3 -right-3 w-8 h-8 bg-jamaica-yellow rounded-full shadow-lg animate-pulse" style={{ animationDelay: '300ms' }}></div>
            <div className="absolute -bottom-4 -right-4 w-10 h-10 bg-gradient-to-br from-jamaica-yellow/80 to-jamaica-green/60 rounded-xl shadow-lg transform -rotate-12 animate-pulse" style={{ animationDelay: '600ms' }}></div>
            
            {/* Loading Content */}
            <div className="text-center space-y-6">
              {/* Large Loading Icon */}
              <div className="relative mx-auto w-20 h-20">
                <div className="w-full h-full bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl flex items-center justify-center shadow-inner">
                  <LoadingSpinner size="lg" className="text-jamaica-green" />
                </div>
                {/* Pulsing Ring */}
                <div className="absolute inset-0 rounded-2xl border-2 border-jamaica-green/20 animate-ping"></div>
              </div>
              
              {/* Loading Text */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">Loading Dashboard</h3>
                <p className="text-gray-600">Preparing your affiliate insights...</p>
              </div>
              
              {/* Animated Progress Bar */}
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-jamaica-green to-jamaica-yellow rounded-full animate-pulse"></div>
                </div>
              </div>
              
              {/* Loading Steps */}
              <div className="flex justify-center space-x-2">
                <div className="w-2 h-2 bg-jamaica-green rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-jamaica-green rounded-full animate-bounce" style={{ animationDelay: '100ms' }}></div>
                <div className="w-2 h-2 bg-jamaica-green rounded-full animate-bounce" style={{ animationDelay: '200ms' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <Card>
            <CardContent className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Error Loading Dashboard</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={refreshDashboard}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show application status if not approved
  if (!dashboardData || application?.status !== 'approved') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <Card>
            <CardContent className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Application Status</h2>
              {application?.status === 'pending' && (
                <>
                  <Badge className="bg-yellow-500 mb-4">Pending Review</Badge>
                  <p className="text-gray-600">
                    Your affiliate application is under review. We'll notify you when it's approved.
                  </p>
                </>
              )}
              {application?.status === 'rejected' && (
                <>
                  <Badge variant="destructive" className="mb-4">Application Declined</Badge>
                  <p className="text-gray-600">
                    Your affiliate application was not approved at this time.
                  </p>
                </>
              )}
              {!application && (
                <>
                  <p className="text-gray-600 mb-4">You haven't applied to become an affiliate yet.</p>
                  <Button onClick={() => navigate('/affiliate/signup')} className="bg-jamaica-green hover:bg-jamaica-green/90">
                    Apply to Become an Affiliate
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Generate SEO data for Affiliate Dashboard page
  const seoData = validateSEOData(getAffiliateDashboardSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={seoData.url} />
      </Helmet>
      
      <div className="min-h-screen relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes */}
        <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
        <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
        <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
        
        {/* Triangle Decorative Elements */}
        <div className="absolute top-1/4 right-10 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10 animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/3 left-16 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15 animate-bounce delay-1200"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(0, 116, 47, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 116, 47, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      <div className="relative container mx-auto px-4 max-w-6xl py-8">
        {/* Enhanced Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-5 h-5 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                <DollarSign className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-4 py-1.5 rounded-2xl border border-jamaica-green/20 shadow-lg">
              <span className="text-sm font-bold text-jamaica-black flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-jamaica-green" />
                Affiliate Dashboard
              </span>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
            <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent px-2">
              Welcome Back, Affiliate!
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
            Track your performance and earnings with our 
            <span className="text-jamaica-green font-semibold"> real-time dashboard</span>
          </p>
        </div>

        {/* Referral Link Card */}
        <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 hover:shadow-2xl transition-all duration-500 group mb-8">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5 rounded-2xl"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
          
          <div className="relative p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                <ExternalLink className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Your Referral Link</h3>
                <p className="text-sm text-gray-600">Share this link to earn commissions on sales</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Input 
                readOnly 
                value={`https://jampackd.store?ref=${application.referralCode}`}
                className="font-mono text-sm bg-white/80 border-jamaica-green/20"
              />
              <Button onClick={copyReferralLink} variant="outline" className="border-jamaica-green/30 hover:bg-jamaica-green/10">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Discount Codes Card */}
        {discountCodes.length > 0 && (
          <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 hover:shadow-2xl transition-all duration-500 group mb-8">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5 rounded-2xl"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
            
            <div className="relative p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                  <Tag className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Your Discount Codes</h3>
                  <p className="text-sm text-gray-600">Share these codes to earn commissions on sales</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {discountCodes.map((code: any) => (
                  <div key={code.id} className="bg-white/60 rounded-xl border border-white/40 p-4 group/card hover:bg-white/80 transition-all duration-300">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline" 
                          className="bg-jamaica-green/10 text-jamaica-green border-jamaica-green/30 font-mono font-bold"
                        >
                          {code.code}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(code.code);
                            toast({
                              title: 'Copied!',
                              description: 'Discount code copied to clipboard',
                            });
                          }}
                          className="h-6 w-6 p-0 opacity-0 group-hover/card:opacity-100 transition-opacity"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <Badge 
                        className={`text-xs ${
                          code.status === 'active' 
                            ? 'bg-green-100 text-green-800 border-green-300' 
                            : code.status === 'expired'
                            ? 'bg-gray-100 text-gray-800 border-gray-300'
                            : 'bg-yellow-100 text-yellow-800 border-yellow-300'
                        }`}
                        variant="outline"
                      >
                        {code.status === 'active' ? '🟢 Active' : 
                         code.status === 'expired' ? '🔴 Expired' : 
                         '🟡 Inactive'}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Discount:</span>
                        <span className="font-medium text-gray-900">
                          {code.type === 'percentage' ? `${code.value}% off` : 
                           code.type === 'fixed' ? formatCurrency(code.value) + ' USD off' : 
                           'Free shipping'}
                        </span>
                      </div>
                      
                      {code.minOrderAmount > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Min order:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(code.minOrderAmount)} USD</span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Usage:</span>
                        <span className={`font-medium ${code.currentUses >= code.maxUses ? 'text-red-600' : 'text-gray-900'}`}>
                          {code.currentUses}/{code.maxUses}
                        </span>
                      </div>
                      
                      {code.endDate && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Expires:</span>
                          <span className={`font-medium ${
                            new Date(code.endDate.seconds * 1000) < new Date() 
                              ? 'text-red-600' 
                              : new Date(code.endDate.seconds * 1000) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                              ? 'text-orange-600'
                              : 'text-gray-900'
                          }`}>
                            {new Date(code.endDate.seconds * 1000).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {code.description && (
                      <div className="mt-3 pt-3 border-t border-white/40">
                        <p className="text-xs text-gray-600 line-clamp-2">{code.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {discountCodes.length > 6 && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600">
                    Showing first 6 discount codes. Total: {discountCodes.length}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className={`relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border transition-all duration-500 group ${
            dateFilter.startDate && dateFilter.endDate 
              ? 'border-amber-300 hover:border-amber-400 hover:shadow-2xl' 
              : 'border-white/40 hover:shadow-2xl'
          }`}>
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r transition-opacity duration-500 rounded-t-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400 via-orange-400 to-amber-400 opacity-100'
                : 'from-blue-500 via-blue-400 to-blue-500 opacity-0 group-hover:opacity-100'
            }`}></div>
            <div className={`absolute inset-0 bg-gradient-to-r rounded-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400/5 via-orange-400/5 to-amber-400/5'
                : 'from-blue-500/5 via-transparent to-blue-400/5'
            }`}></div>
            <div className={`absolute top-0 right-0 w-20 h-20 rounded-full -translate-y-10 translate-x-10 ${
              dateFilter.startDate && dateFilter.endDate
                ? 'bg-amber-500/10'
                : 'bg-blue-500/10'
            }`}></div>
            
            <div className="relative p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-gray-900">Total Clicks</h3>
                    {dateFilter.startDate && dateFilter.endDate && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                        Filtered
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    {dateFilter.startDate && dateFilter.endDate 
                      ? `${formatDate(new Date(dateFilter.startDate + 'T00:00:00'))} - ${formatDate(new Date(dateFilter.endDate + 'T23:59:59'))}` 
                      : 'All time data'
                    }
                  </p>
                </div>
              </div>
              <div className="text-3xl font-bold text-blue-600 relative">
                {isLoadingFiltered ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <span className="text-lg text-gray-500">Loading...</span>
                  </div>
                ) : (
                  dateFilter.startDate && dateFilter.endDate ? filteredStats.clicks : dashboardData.totalClicks
                )}
              </div>
            </div>
          </div>

          <div className={`relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border transition-all duration-500 group ${
            dateFilter.startDate && dateFilter.endDate 
              ? 'border-amber-300 hover:border-amber-400 hover:shadow-2xl' 
              : 'border-white/40 hover:shadow-2xl'
          }`}>
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r transition-opacity duration-500 rounded-t-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400 via-orange-400 to-amber-400 opacity-100'
                : 'from-green-500 via-green-400 to-green-500 opacity-0 group-hover:opacity-100'
            }`}></div>
            <div className={`absolute inset-0 bg-gradient-to-r rounded-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400/5 via-orange-400/5 to-amber-400/5'
                : 'from-green-500/5 via-transparent to-green-400/5'
            }`}></div>
            <div className={`absolute top-0 right-0 w-20 h-20 rounded-full -translate-y-10 translate-x-10 ${
              dateFilter.startDate && dateFilter.endDate
                ? 'bg-amber-500/10'
                : 'bg-green-500/10'
            }`}></div>
            
            <div className="relative p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-gray-900">Conversions</h3>
                    {dateFilter.startDate && dateFilter.endDate && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                        Filtered
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    {dateFilter.startDate && dateFilter.endDate 
                      ? `${formatDate(new Date(dateFilter.startDate + 'T00:00:00'))} - ${formatDate(new Date(dateFilter.endDate + 'T23:59:59'))}` 
                      : 'All time data'
                    }
                  </p>
                </div>
              </div>
              <div className="text-3xl font-bold text-green-600 relative">
                {isLoadingFiltered ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
                    <span className="text-lg text-gray-500">Loading...</span>
                  </div>
                ) : (
                  dateFilter.startDate && dateFilter.endDate ? filteredStats.conversions : dashboardData.totalConversions
                )}
              </div>
            </div>
          </div>

          <div className={`relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border transition-all duration-500 group ${
            dateFilter.startDate && dateFilter.endDate 
              ? 'border-amber-300 hover:border-amber-400 hover:shadow-2xl' 
              : 'border-white/40 hover:shadow-2xl'
          }`}>
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r transition-opacity duration-500 rounded-t-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400 via-orange-400 to-amber-400 opacity-100'
                : 'from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100'
            }`}></div>
            <div className={`absolute inset-0 bg-gradient-to-r rounded-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400/5 via-orange-400/5 to-amber-400/5'
                : 'from-jamaica-green/5 via-transparent to-jamaica-yellow/5'
            }`}></div>
            <div className={`absolute top-0 right-0 w-20 h-20 rounded-full -translate-y-10 translate-x-10 ${
              dateFilter.startDate && dateFilter.endDate
                ? 'bg-amber-500/10'
                : 'bg-jamaica-green/10'
            }`}></div>
            
            <div className="relative p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-gray-900">Total Earnings</h3>
                    {dateFilter.startDate && dateFilter.endDate && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                        Filtered
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    {dateFilter.startDate && dateFilter.endDate 
                      ? `${formatDate(new Date(dateFilter.startDate + 'T00:00:00'))} - ${formatDate(new Date(dateFilter.endDate + 'T23:59:59'))}` 
                      : 'All time data'
                    }
                  </p>
                </div>
              </div>
              <div className="text-3xl font-bold text-jamaica-green relative">
                {isLoadingFiltered ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-jamaica-green"></div>
                    <span className="text-lg text-gray-500">Loading...</span>
                  </div>
                ) : (
                  formatCurrency(dateFilter.startDate && dateFilter.endDate ? filteredStats.totalEarnings : dashboardData.totalEarnings) + ' USD'
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Filter */}
        <div className={`relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border transition-all duration-500 group mb-8 ${
          dateFilter.startDate && dateFilter.endDate 
            ? 'border-amber-300 hover:border-amber-400 hover:shadow-2xl' 
            : 'border-white/40 hover:shadow-2xl'
        }`}>
          <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r transition-opacity duration-500 rounded-t-2xl ${
            dateFilter.startDate && dateFilter.endDate
              ? 'from-amber-400 via-orange-400 to-amber-400 opacity-100'
              : 'from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100'
          }`}></div>
          {/* Background decoration */}
          <div className={`absolute inset-0 bg-gradient-to-r rounded-2xl ${
            dateFilter.startDate && dateFilter.endDate
              ? 'from-amber-400/5 via-orange-400/5 to-amber-400/5'
              : 'from-jamaica-yellow/5 via-transparent to-jamaica-green/5'
          }`}></div>
          <div className={`absolute top-0 left-0 w-28 h-28 rounded-full -translate-y-14 -translate-x-14 ${
            dateFilter.startDate && dateFilter.endDate
              ? 'bg-amber-500/10'
              : 'bg-jamaica-green/10'
          }`}></div>
          <div className={`absolute bottom-0 right-0 w-20 h-20 rounded-full translate-y-10 translate-x-10 ${
            dateFilter.startDate && dateFilter.endDate
              ? 'bg-orange-500/10'
              : 'bg-jamaica-yellow/10'
          }`}></div>
          
          <div className={`relative transition-all duration-300 ${
            isFilterCollapsed ? 'px-6 pt-6 pb-3' : 'p-6'
          }`}>
            <div 
              className="flex items-center gap-4 mb-6 cursor-pointer hover:bg-gray-50/50 -mx-2 px-2 py-2 rounded-lg transition-colors duration-200"
              onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-jamaica-yellow to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Filter className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold text-gray-900">Analytics Filter</h3>
                  {dateFilter.startDate && dateFilter.endDate && (
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                      Active
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {dateFilter.startDate && dateFilter.endDate 
                    ? `Currently showing: ${formatDate(new Date(dateFilter.startDate + 'T00:00:00'))} - ${formatDate(new Date(dateFilter.endDate + 'T23:59:59'))}`
                    : 'Select a date range to analyze your performance'
                  }
                </p>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {(dateFilter.startDate || dateFilter.endDate) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setDateFilter({ startDate: '', endDate: '' });
                      setFilteredStats({ clicks: 0, conversions: 0, totalEarnings: 0 });
                      setFilteredCommissions([]);
                      setFilteredPayouts([]);
                      setActiveFilter(null);
                      setCommissionsCurrentPage(1);
                      setPayoutsCurrentPage(1);
                    }}
                    className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/80"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center border transition-colors duration-200 cursor-pointer ${
                    dateFilter.startDate && dateFilter.endDate
                      ? 'bg-amber-100 border-amber-300 hover:bg-amber-200'
                      : 'bg-jamaica-green/10 border-jamaica-green/20 hover:bg-jamaica-green/20'
                  }`}
                  onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
                >
                  {isFilterCollapsed ? (
                    <ChevronDown className={`h-5 w-5 ${
                      dateFilter.startDate && dateFilter.endDate
                        ? 'text-amber-800'
                        : 'text-jamaica-green'
                    }`} />
                  ) : (
                    <ChevronUp className={`h-5 w-5 ${
                      dateFilter.startDate && dateFilter.endDate
                        ? 'text-amber-800'
                        : 'text-jamaica-green'
                    }`} />
                  )}
                </div>
              </div>
            </div>

            {/* Collapsible Filter Content */}
            <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
              isFilterCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
            }`}>
              {/* Quick Filter Buttons */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDateFilter({ startDate: '', endDate: '' });
                  setFilteredStats({ clicks: 0, conversions: 0, totalEarnings: 0 });
                  setFilteredCommissions([]);
                  setFilteredPayouts([]);
                  setActiveFilter(null);
                  setCommissionsCurrentPage(1);
                  setPayoutsCurrentPage(1);
                  // Refresh analytics data to show all-time data
                  loadAnalyticsData();
                }}
                className={`transition-all duration-200 ${
                  !activeFilter
                    ? 'bg-jamaica-green text-white border-jamaica-green hover:bg-jamaica-green/90'
                    : 'bg-white/60 border-jamaica-green/20 hover:bg-jamaica-green/10 hover:border-jamaica-green/40'
                }`}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                All Time
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  const newFilter = {
                    startDate: lastWeek.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                  };
                  setDateFilter(newFilter);
                  setActiveFilter('last7days');
                  setCommissionsCurrentPage(1);
                  setPayoutsCurrentPage(1);
                }}
                className={`transition-all duration-200 ${
                  activeFilter === 'last7days'
                    ? 'bg-jamaica-green text-white border-jamaica-green hover:bg-jamaica-green/90'
                    : 'bg-white/60 border-jamaica-green/20 hover:bg-jamaica-green/10 hover:border-jamaica-green/40'
                }`}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Last 7 Days
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                  const newFilter = {
                    startDate: lastMonth.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                  };
                  setDateFilter(newFilter);
                  setActiveFilter('last30days');
                  setCommissionsCurrentPage(1);
                  setPayoutsCurrentPage(1);
                }}
                className={`transition-all duration-200 ${
                  activeFilter === 'last30days'
                    ? 'bg-jamaica-green text-white border-jamaica-green hover:bg-jamaica-green/90'
                    : 'bg-white/60 border-jamaica-green/20 hover:bg-jamaica-green/10 hover:border-jamaica-green/40'
                }`}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Last 30 Days
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                  const newFilter = {
                    startDate: startOfMonth.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                  };
                  setDateFilter(newFilter);
                  setActiveFilter('thismonth');
                  setCommissionsCurrentPage(1);
                  setPayoutsCurrentPage(1);
                }}
                className={`transition-all duration-200 ${
                  activeFilter === 'thismonth'
                    ? 'bg-jamaica-green text-white border-jamaica-green hover:bg-jamaica-green/90'
                    : 'bg-white/60 border-jamaica-green/20 hover:bg-jamaica-green/10 hover:border-jamaica-green/40'
                }`}
              >
                <Calendar className="h-4 w-4 mr-2" />
                This Month
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                  const newFilter = {
                    startDate: lastMonthStart.toISOString().split('T')[0],
                    endDate: lastMonthEnd.toISOString().split('T')[0]
                  };
                  setDateFilter(newFilter);
                  setActiveFilter('lastmonth');
                  setCommissionsCurrentPage(1);
                  setPayoutsCurrentPage(1);
                }}
                className={`transition-all duration-200 ${
                  activeFilter === 'lastmonth'
                    ? 'bg-jamaica-green text-white border-jamaica-green hover:bg-jamaica-green/90'
                    : 'bg-white/60 border-jamaica-green/20 hover:bg-jamaica-green/10 hover:border-jamaica-green/40'
                }`}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Last Month
              </Button>
            </div>

            {/* Custom Date Range */}
            <div className="bg-white/40 rounded-xl border border-white/60 p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-jamaica-green/20 rounded-lg flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-jamaica-green" />
                </div>
                <span className="text-sm font-semibold text-gray-700">Custom Date Range</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <span>Start Date</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
                    className="bg-white/80 border-jamaica-green/20 focus:border-jamaica-green/50 focus:ring-jamaica-green/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <span>End Date</span>
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
                    min={dateFilter.startDate}
                    className="bg-white/80 border-jamaica-green/20 focus:border-jamaica-green/50 focus:ring-jamaica-green/20"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => {
                    setActiveFilter('custom');
                    setCommissionsCurrentPage(1);
                    setPayoutsCurrentPage(1);
                  }} 
                  disabled={isLoadingFiltered || !dateFilter.startDate || !dateFilter.endDate}
                  className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transition-all duration-300 flex-1"
                >
                  {isLoadingFiltered ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Filter className="mr-2 h-4 w-4" />
                      Apply Custom Filter
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={refreshDashboard} 
                  className="border-jamaica-green/30 hover:bg-jamaica-green/10 px-6"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh All
                </Button>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Commission Source Analytics */}
        {sourceAnalytics && (
          <div className={`relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border transition-all duration-500 group mb-8 ${
            dateFilter.startDate && dateFilter.endDate
              ? 'border-amber-300 hover:border-amber-400 hover:shadow-2xl'
              : 'border-white/40 hover:shadow-2xl'
          }`}>
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r transition-opacity duration-500 rounded-t-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400 via-orange-400 to-amber-400 opacity-100'
                : 'from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100'
            }`}></div>
            <div className={`absolute inset-0 bg-gradient-to-r rounded-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400/5 via-orange-400/5 to-amber-400/5'
                : 'from-jamaica-green/5 via-transparent to-jamaica-yellow/5'
            }`}></div>
            <div className={`absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-16 translate-x-16 ${
              dateFilter.startDate && dateFilter.endDate
                ? 'bg-amber-500/10'
                : 'bg-jamaica-yellow/10'
            }`}></div>
            <div className={`absolute bottom-0 left-0 w-24 h-24 rounded-full translate-y-12 -translate-x-12 ${
              dateFilter.startDate && dateFilter.endDate
                ? 'bg-orange-500/10'
                : 'bg-jamaica-green/10'
            }`}></div>
            
            <div className="relative p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold text-gray-900">Commission Source Analytics</h3>
                    {dateFilter.startDate && dateFilter.endDate && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                        Filtered
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {dateFilter.startDate && dateFilter.endDate 
                      ? `${formatDate(new Date(dateFilter.startDate + 'T00:00:00'))} - ${formatDate(new Date(dateFilter.endDate + 'T23:59:59'))}` 
                      : 'Performance breakdown by referral links vs discount codes'
                    }
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Pie Chart */}
                <div className="bg-white/60 rounded-xl border border-white/40 p-6">
                  <div className="text-center mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Commission Distribution</h4>
                    <div className="h-64">
                      {sourceAnalytics.summary.totalCommissions > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Referral Links', value: sourceAnalytics.summary.linkPercentage, count: sourceAnalytics.linkStats.count },
                                { name: 'Discount Codes', value: sourceAnalytics.summary.discountPercentage, count: sourceAnalytics.discountStats.count }
                              ]}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              label={({value, count}) => `${value.toFixed(1)}% (${count})`}
                            >
                              <Cell fill="#3B82F6" />
                              <Cell fill="#8B5CF6" />
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-gray-500">No commission data available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="space-y-4">
                  <div className="bg-white/60 rounded-xl border border-white/40 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="font-medium text-gray-900">Referral Links</span>
                      </div>
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                        {sourceAnalytics.linkStats.count} sales
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Total Earnings</p>
                        <p className="font-bold text-jamaica-green">{formatCurrency(sourceAnalytics.linkStats.totalEarnings)} USD</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Avg Commission</p>
                        <p className="font-bold text-gray-900">{formatCurrency(sourceAnalytics.linkStats.avgCommission)} USD</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/60 rounded-xl border border-white/40 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                        <span className="font-medium text-gray-900">Discount Codes</span>
                      </div>
                      <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                        {sourceAnalytics.discountStats.count} sales
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Total Earnings</p>
                        <p className="font-bold text-jamaica-green">{formatCurrency(sourceAnalytics.discountStats.totalEarnings)} USD</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Avg Commission</p>
                        <p className="font-bold text-gray-900">{formatCurrency(sourceAnalytics.discountStats.avgCommission)} USD</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Commission Records */}
        <div id="commissions-table" className={`relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border transition-all duration-500 group mb-8 ${
          dateFilter.startDate && dateFilter.endDate
            ? 'border-amber-300 hover:border-amber-400 hover:shadow-2xl'
            : 'border-white/40 hover:shadow-2xl'
        }`}>
          <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r transition-opacity duration-500 rounded-t-2xl ${
            dateFilter.startDate && dateFilter.endDate
              ? 'from-amber-400 via-orange-400 to-amber-400 opacity-100'
              : 'from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100'
          }`}></div>
          {/* Background decoration */}
          <div className={`absolute inset-0 bg-gradient-to-r rounded-2xl ${
            dateFilter.startDate && dateFilter.endDate
              ? 'from-amber-400/5 via-orange-400/5 to-amber-400/5'
              : 'from-jamaica-yellow/5 via-transparent to-jamaica-green/5'
          }`}></div>
          <div className={`absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-16 translate-x-16 ${
            dateFilter.startDate && dateFilter.endDate
              ? 'bg-amber-500/10'
              : 'bg-jamaica-yellow/10'
          }`}></div>
          <div className={`absolute bottom-0 left-0 w-24 h-24 rounded-full translate-y-12 -translate-x-12 ${
            dateFilter.startDate && dateFilter.endDate
              ? 'bg-orange-500/10'
              : 'bg-jamaica-green/10'
          }`}></div>
          
          <div className="relative p-6">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-xl font-bold text-gray-900">Commission Records</h3>
                    {dateFilter.startDate && dateFilter.endDate && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                        Filtered
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {dateFilter.startDate && dateFilter.endDate 
                      ? `${formatDate(new Date(dateFilter.startDate + 'T00:00:00'))} - ${formatDate(new Date(dateFilter.endDate + 'T23:59:59'))}` 
                      : 'All commission records'
                    }
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-gray-900">
                  {dateFilter.startDate && dateFilter.endDate ? filteredCommissions.length : dashboardData.commissionRecords.length}
                </p>
                <p className="text-xs text-gray-500">
                  {dateFilter.startDate && dateFilter.endDate && filteredCommissions.length !== dashboardData.commissionRecords.length
                    ? `of ${dashboardData.commissionRecords.length} total`
                    : 'records'
                  }
                </p>
              </div>
            </div>
            
            {isLoadingFiltered ? (
              <div className="bg-white/60 rounded-xl border border-white/40 p-12">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jamaica-green"></div>
                  <p className="text-gray-500 font-medium">Loading commission records...</p>
                </div>
              </div>
            ) : (dateFilter.startDate && dateFilter.endDate ? filteredCommissions : dashboardData.commissionRecords).length > 0 ? (
              <div className="bg-white/60 rounded-xl border border-white/40 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-jamaica-green/5">
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Order ID</TableHead>
                      <TableHead className="font-semibold">Source</TableHead>
                      <TableHead className="font-semibold">Order Value</TableHead>
                      <TableHead className="font-semibold">Order Status</TableHead>
                      <TableHead className="font-semibold">Commission</TableHead>
                      <TableHead className="font-semibold">Payout Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getPaginatedCommissions().map((commission) => (
                      <TableRow key={commission.id} className="hover:bg-jamaica-green/5">
                        <TableCell>{formatDate(commission.createdAt)}</TableCell>
                        <TableCell className="font-mono text-sm text-gray-600">{commission.orderId.substring(0, 8)}...</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${
                            commission.source === 'discount_code' 
                              ? 'bg-purple-100 text-purple-800 border-purple-300' 
                              : 'bg-blue-100 text-blue-800 border-blue-300'
                          }`}>
                            {commission.source === 'discount_code' ? 'Discount Code' : 'Referral Link'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{formatCurrency(commission.orderValue)} USD</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${
                            commission.paymentStatus === 'succeeded' ? 'bg-green-100 text-green-800' :
                            commission.paymentStatus === 'refunded' ? 'bg-red-100 text-red-800' :
                            commission.paymentStatus === 'failed' ? 'bg-red-100 text-red-800' :
                            commission.paymentStatus === 'refund_pending' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {commission.paymentStatus === 'succeeded' ? 'Paid' : 
                             commission.paymentStatus === 'refunded' ? 'Refunded' : 
                             commission.paymentStatus || 'pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className={`font-bold ${commission.commissionAmount >= 0 ? 'text-jamaica-green' : 'text-red-600'}`}>
                          {formatCurrency(commission.commissionAmount)} USD
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${
                            commission.status === 'paid' ? 'bg-green-100 text-green-800' :
                            commission.status === 'clawback' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {commission.status === 'paid' ? 'Paid' : 
                             commission.status === 'clawback' ? 'Retracted' : 
                             'Pending'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Commission Pagination */}
                <TablePagination
                  currentPage={commissionsCurrentPage}
                  totalPages={getCommissionsTotalPages()}
                  onPageChange={handleCommissionsPageChange}
                  loading={isLoadingFiltered}
                  totalItems={dateFilter.startDate && dateFilter.endDate ? filteredCommissions.length : dashboardData.commissionRecords.length}
                  itemsPerPage={commissionsPageSize}
                />
              </div>
            ) : (
              <div className="text-center py-12 bg-white/40 rounded-xl border border-white/40">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">No commission records found for the selected period.</p>
                <p className="text-gray-400 text-sm mt-1">Start sharing your referral link to earn commissions!</p>
              </div>
            )}
          </div>
        </div>

        {/* Past Payouts */}
        {dashboardData.payoutRecords.length > 0 && (
          <div id="payouts-table" className={`relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border transition-all duration-500 group mb-8 ${
            dateFilter.startDate && dateFilter.endDate 
              ? 'border-amber-300 hover:border-amber-400 hover:shadow-2xl' 
              : 'border-white/40 hover:shadow-2xl'
          }`}>
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r transition-opacity duration-500 rounded-t-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400 via-orange-400 to-amber-400 opacity-100'
                : 'from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100'
            }`}></div>
            {/* Background decoration */}
            <div className={`absolute inset-0 bg-gradient-to-r rounded-2xl ${
              dateFilter.startDate && dateFilter.endDate
                ? 'from-amber-400/5 via-orange-400/5 to-amber-400/5'
                : 'from-jamaica-green/5 via-transparent to-jamaica-yellow/5'
            }`}></div>
            <div className={`absolute top-0 left-0 w-28 h-28 rounded-full -translate-y-14 -translate-x-14 ${
              dateFilter.startDate && dateFilter.endDate
                ? 'bg-amber-500/10'
                : 'bg-jamaica-green/10'
            }`}></div>
            <div className={`absolute bottom-0 right-0 w-20 h-20 rounded-full translate-y-10 translate-x-10 ${
              dateFilter.startDate && dateFilter.endDate
                ? 'bg-orange-500/10'
                : 'bg-jamaica-yellow/10'
            }`}></div>
            
            <div className="relative p-6">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-xl font-bold text-gray-900">Past Payouts</h3>
                      {dateFilter.startDate && dateFilter.endDate && (
                        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                          Filtered
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {dateFilter.startDate && dateFilter.endDate 
                        ? `${formatDate(new Date(dateFilter.startDate + 'T00:00:00'))} - ${formatDate(new Date(dateFilter.endDate + 'T23:59:59'))}` 
                        : 'Your payout history'
                      }
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                    {dateFilter.startDate && dateFilter.endDate ? filteredPayouts.length : dashboardData.payoutRecords.length}
                  </p>
                  <p className="text-xs text-gray-500">
                    {dateFilter.startDate && dateFilter.endDate && filteredPayouts.length !== dashboardData.payoutRecords.length
                      ? `of ${dashboardData.payoutRecords.length} total`
                      : 'records'
                    }
                  </p>
                </div>
              </div>
              {isLoadingFiltered ? (
                <div className="bg-white/60 rounded-xl border border-white/40 p-12">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jamaica-green"></div>
                    <p className="text-gray-500 font-medium">Loading payout records...</p>
                  </div>
                </div>
              ) : (dateFilter.startDate && dateFilter.endDate ? filteredPayouts : dashboardData.payoutRecords).length > 0 ? (
                <div className="bg-white/60 rounded-xl border border-white/40 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-jamaica-green/5">
                        <TableHead className="font-semibold">Date</TableHead>
                        <TableHead className="font-semibold">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getPaginatedPayouts().map((payout) => (
                        <TableRow key={payout.id} className="hover:bg-jamaica-green/5">
                          <TableCell>{formatDate(payout.paidAt)}</TableCell>
                          <TableCell className="font-medium text-jamaica-green">{formatCurrency(payout.amount)} USD</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Payouts Pagination */}
                  <TablePagination
                    currentPage={payoutsCurrentPage}
                    totalPages={getPayoutsTotalPages()}
                    onPageChange={handlePayoutsPageChange}
                    loading={isLoadingFiltered}
                    totalItems={dateFilter.startDate && dateFilter.endDate ? filteredPayouts.length : dashboardData.payoutRecords.length}
                    itemsPerPage={payoutsPageSize}
                  />
                </div>
              ) : (
                <div className="text-center py-12 bg-white/40 rounded-xl border border-white/40">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No payout records found for the selected period.</p>
                  <p className="text-gray-400 text-sm mt-1">Payouts will appear here once they're processed.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payout Information */}
        <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 hover:shadow-2xl transition-all duration-500 group mb-8">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5 rounded-2xl"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
          
          <div className="relative p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Payout Information</h3>
                <p className="text-sm text-gray-600">Payment schedule and commission details</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="p-4 bg-jamaica-green/5 rounded-xl border border-jamaica-green/10">
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-gray-900">
                  <Clock className="h-4 w-4 text-jamaica-green" />
                  Next Payout Date
                </h4>
                <p className="text-lg font-medium text-gray-700">
                  {dashboardData.nextPayoutDate ? formatDate(dashboardData.nextPayoutDate) : 'Not scheduled'}
                </p>
              </div>
              <div className="p-4 bg-jamaica-yellow/5 rounded-xl border border-jamaica-yellow/10">
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-gray-900">
                  <TrendingUp className="h-4 w-4 text-jamaica-green" />
                  Commission Rate
                </h4>
                <p className="text-lg font-medium text-gray-700">{dashboardData.commissionPercentage}%</p>
              </div>
            </div>

            {/* Payment Information */}
            {application?.paymentInfo && (
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <h4 className="font-semibold mb-3 flex items-center gap-2 text-gray-900">
                  <CreditCard className="h-4 w-4 text-jamaica-green" />
                  Payment Method
                </h4>
                
                {application.paymentInfo.type === 'paypal' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                        <span className="text-xs font-bold text-blue-600">PP</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">PayPal</span>
                    </div>
                    <p className="text-sm font-medium text-gray-700 font-mono">
                      {application.paymentInfo.paypalEmail}
                    </p>
                  </div>
                ) : application.paymentInfo.type === 'jamaican_bank' && application.paymentInfo.bankInfo ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 bg-jamaica-green/20 rounded flex items-center justify-center">
                        <span className="text-xs font-bold text-jamaica-green">JM</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">Jamaican Bank Account</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-gray-700">
                        {application.paymentInfo.bankInfo.bankName} - {application.paymentInfo.bankInfo.branch}
                      </p>
                      <p className="text-gray-600">
                        {application.paymentInfo.bankInfo.accountHolderName}
                      </p>
                      <p className="font-mono text-gray-600">
                        {application.paymentInfo.bankInfo.accountType.charAt(0).toUpperCase() + application.paymentInfo.bankInfo.accountType.slice(1)} ****{application.paymentInfo.bankInfo.accountNumber.slice(-4)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Payment information not available</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModalWrapper 
        isOpen={authModalOpen} 
        onOpenChange={setAuthModalOpen}
        defaultTab="login"
      />
    </div>
    </>
  );
};

export default AffiliateDashboard;